import React from 'react';
import { useTranslation } from 'react-i18next';

const Eprc = () => {
  const { t } = useTranslation('help');

  return (
    <div style={styles.container}>
      {/* Title */}
      <h2 style={styles.title}>
        {t('eprc.title')}
      </h2>

      {/* Section (a) - Purpose */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(a)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('eprc.labels.purpose', 'Purpose:-')}
            </strong>
            {t(
              'eprc.descriptions.purpose',
              'Used for regulation-compliance testing. The detector-output endpoint receives the prompt and returns the processed compliance result. Use this endpoint pattern when the target API is intended to be checked against selected regulation or policy datasets. The endpoint still behaves like an AI App endpoint, but the request and response mappings are usually tuned for compliance-test prompts and result extraction.'
            )}
          </p>
        </div>
      </div>

      {/* Section (b) - Endpoint */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(b)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('eprc.labels.endpoint', 'Endpoint:-')}
            </strong>{' '}
            {t(
              'eprc.descriptions.endpoint',
              'An endpoint is the saved connection definition for the AI system WhiteHaX will test. It tells WhiteHaX what type of AI target it is testing, where the target is reachable, and how requests and responses should be prepared or interpreted'
            )}
          </p>
          <div>
            <a 
              href="http://52.53.127.59:5000/detector-output" 
              style={styles.link} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              http://52.53.127.59:5000/detector-output
            </a>
          </div>
        </div>
      </div>

      {/* Section (c) - Request Json */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(c)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('eprc.labels.requestJson', 'Request Json :-')}{' '}
            </strong>
            {t(
              'eprc.descriptions.requestJson',
              'Template for headers, body, prompt insertion path, and data type,Use placeholders such as prompt insertion fields exactly as the WhiteHaX UI expects. Keep auth headers current and avoid pasting long-lived secrets into screenshots or shared docs.'
            )}
          </p>

          {/* Request JSON Code Block */}
          <div style={styles.codeBlock}>
            <div>{'{'}</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"headers"</span>: {'{'}
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"Authorization"</span>: <span style={styles.jsonString}>"Bearer xxx"</span>,
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"Content-Type"</span>: <span style={styles.jsonString}>"application/json"</span>
            </div>
            <div style={styles.indent1}>{'}'},</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"request_template"</span>: {'{'}
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"user_input"</span>: <span style={styles.jsonString}>"{"{{PROMPT}}"}"</span>
            </div>
            <div style={styles.indent1}>{'}'},</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"prompt_path"</span>:<span style={styles.jsonString}>"user_input"</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"data_type"</span>:<span style={styles.jsonString}>"form"</span>
            </div>
            <div>{'}'}</div>
          </div>
        </div>
      </div>

      {/* Section (C) - Response Json */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(C)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('eprc.labels.responseJson', 'Response Json :-')}{' '}
            </strong>
            {t(
              'eprc.descriptions.responseJson',
              'Template that tells WhiteHaX where the model output lives ,It maps the actual response body field that contains the answer.'
            )}
          </p>

          {/* Response JSON Code Block */}
          <div style={styles.codeBlock}>
            <div>{'{'}</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"data"</span>: <span style={styles.jsonString}>"{"{{RESP}}"}"</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"error"</span>: <span style={styles.jsonBoolean}>false</span>
            </div>
            <div>{'}'}</div>
          </div>
        </div>
      </div>

      {/* Section (D) - Expected Test Results */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(D)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('eprc.labels.expectedResults', 'Expected Test Results against this simulator:-')}{' '}
            </strong>
            {t(
              'eprc.descriptions.expectedResults',
              'The expected test result should correctly reflect the configured regulation or policy dataset, identifying compliance and non-compliance cases accurately.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

// Component Styles (Dark Theme)
const styles = {
  container: {
    maxWidth: '850px',
    margin: '0 auto',
    padding: '24px',
    color: '#e0e0e0',
    backgroundColor: '#181a1f',
    borderRadius: '8px',
    fontFamily: 'Segoe UI, Roboto, sans-serif'
  },
  title: {
    margin: '0 0 20px 0',
    fontWeight: 'bold',
    color: '#ffffff',
    fontSize: '20px'
  },
  section: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '15px'
  },
  sectionLabel: {
    width: '32px',
    flexShrink: 0,
    fontWeight: 'bold',
    color: '#a0a0a0'
  },
  sectionContent: {
    flexGrow: 1
  },
  paragraph: {
    margin: '0 0 10px 0',
    lineHeight: '1.6',
    color: '#cccccc',
    fontSize: '14px'
  },
  strongText: {
    color: '#ffffff'
  },
  link: {
    color: '#4fa3d1',
    textDecoration: 'underline'
  },
  codeBlock: {
    backgroundColor: '#0d0e11',
    color: '#e0e0e0',
    padding: '16px 20px',
    margin: '12px 0',
    borderRadius: '6px',
    border: '1px solid #2a2d34',
    fontFamily: 'Consolas, Monaco, "Andale Mono", monospace',
    fontSize: '13px',
    lineHeight: '1.5',
    overflowX: 'auto',
    whiteSpace: 'pre'
  },
  indent1: {
    paddingLeft: '20px'
  },
  indent2: {
    paddingLeft: '40px'
  },
  jsonKey: {
    color: '#6fc2ef'
  },
  jsonString: {
    color: '#da70d6'
  },
  jsonBoolean: {
    color: '#da70d6'
  }
};

export default Eprc;